import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  changeEmail,
  changeName,
  changePassword,
  forgotPassword,
  getAuthMe,
  loginWithEmail,
  registerUser,
  resetPassword,
} from "./auth.api";
import { useAuthStore } from "../store/useAuthStore";
import type {
  IRegisterResponse,
  IRegisterPayload,
  IForgotPasswordResponse,
  IForgotPasswordPayload,
  IChangePasswordResponse,
  IChangePasswordPayload,
  IChangeEmailResponse,
  IChangeEmailPayload,
  IChangeNamePayload,
  IChangeNameResponse,
} from "../types/auth.types";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { extractErrorMessage } from "@/lib/errors";

interface LoginParams {
  email: string;
  password: string;
}

export const useLogin = () => {
  const navigate = useNavigate();
  const loginStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: LoginParams) =>
      loginWithEmail(params.email, params.password),

    onSuccess: async (data) => {
      // Clear any cached data from previous sessions (defense in depth)
      queryClient.clear();

      const { user, token, refresh_token } = data;

      // First, store the basic login data
      loginStore.login({
        user: {
          id: user?.id || "",
          mongo_id: user?.mongo_id || "",
          email: user?.email || "",
          username: user?.username || null,
          profile_picture: user?.picture || "",
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          role_name: user?.role_name || null,
        },
        token,
        refresh_token,
      });

      try {
        // Fetch detailed user info including roles and plan_ids
        const userDetails = await getAuthMe();

        // Update user with detailed information from /me endpoint
        loginStore.updateUser({
          id: userDetails.locksmith_id, // Use locksmith_id as the main ID
          mongo_id: userDetails.user_id, // user_id from /me is the mongo_id
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          role_name: userDetails.role_name,
          plan_ids: userDetails.plan_ids,
        });

        // Check roles
        const isAdmin = userDetails.role_name.includes("admin");
        const isManager = userDetails.role_name.includes("manager");

        // Set status BEFORE navigation to prevent route guard issues
        loginStore.setAdminStatus(isAdmin);
        loginStore.setManagerStatus(isManager);

        toast.success("Login successful");

        // Role-based redirection with manager support
        if (isAdmin) {
          navigate({ to: "/admin/dashboard" });
        } else if (isManager) {
          navigate({ to: "/manager/subscription-management" });
        } else {
          navigate({ to: "/keyword-monitoring" });
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
        // Set as non-admin/non-manager on error
        loginStore.setAdminStatus(false);
        loginStore.setManagerStatus(false);
        navigate({ to: "/keyword-monitoring", search: { section: undefined } });
      }
    },

    onError: (err: Error) => {
      toast.error("Failed to login", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useRegister = () => {
  return useMutation<IRegisterResponse, Error, IRegisterPayload>({
    mutationFn: registerUser,
    onSuccess: () => {
      toast.success("Registration successful");
    },

    onError: (err: Error) => {
      toast.error("Failed to register", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useForgotPassword = () => {
  return useMutation<IForgotPasswordResponse, Error, IForgotPasswordPayload>({
    mutationFn: forgotPassword,
    onSuccess: () => {
      toast.success("Password reset email sent successfully");
    },

    onError: (err: Error) => {
      toast.error("Failed to send password reset email", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      toast.success("Password successfully changed!");
    },

    onError: (err: Error) => {
      toast.error("Failed to change password", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useChangePassword = () => {
  return useMutation<IChangePasswordResponse, Error, IChangePasswordPayload>({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success("Password successfully changed!");
    },

    onError: (err: Error) => {
      toast.error("Failed to change password", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useChangeEmail = () => {
  return useMutation<IChangeEmailResponse, Error, IChangeEmailPayload>({
    mutationFn: changeEmail,
    onSuccess: () => {
      toast.success("Email successfully changed!");
    },

    onError: (err: Error) => {
      toast.error("Failed to change email", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useChangeName = () => {
  return useMutation<IChangeNameResponse, Error, IChangeNamePayload>({
    mutationFn: changeName,
    onSuccess: () => {
      toast.success("Name successfully changed!");
    },

    onError: (err: Error) => {
      toast.error("Failed to change name", {
        description: extractErrorMessage(err),
      });
    },
  });
};
